"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Hammer, Layers, Sparkles, Lightbulb, Flame, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  tag: "Structural",
  title: "Architectural layout & wall modifications",
  description: "Load-bearing beam installations, open-concept reconfigurations, and structural adaptations aligned with historic Decatur building standards.",
  specs: "Engineered beam sizing, permitting, floorplan re-alignment",
  icon: Hammer
}, {
  id: 2,
  tag: "Cabinetry",
  title: "Custom handcrafted cabinetry",
  description: "Inset and full-overlay solid wood cabinetry built to your space, with soft-close hardware, integrated pantries, and custom millwork.",
  specs: "Solid hardwood boxes, dovetailed joints, custom finishes",
  icon: Layers
}, {
  id: 3,
  tag: "Surfaces",
  title: "Natural stone & quartz countertops",
  description: "Precision-measured and fabricated quartz, quartzite, and honed marble slabs featuring seamless waterfall edges and matched backsplashes.",
  specs: "Mitered waterfall edges, undermount cutouts, sealant prep",
  icon: Sparkles
}, {
  id: 4,
  tag: "Lighting",
  title: "Architectural & layered lighting",
  description: "Comprehensive electrical planning that balances warm recessed task lights, under-cabinet illumination, and accent island pendants.",
  specs: "Dimmable LED layouts, dedicated circuits, scene switching",
  icon: Lightbulb
}, {
  id: 5,
  tag: "Appliances",
  title: "Appliance integration & commissioning",
  description: "Flush panel-ready refrigeration, precision range ventilation ducting, and exact gas and plumbing connections calibrated before handover.",
  specs: "Zero-clearance fitting, exterior makeup-air venting, testing",
  icon: Flame
}];
export default function Inclusions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whats-included" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary border border-border mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-chart-1" />
            <span className="text-xs font-bold tracking-widest uppercase text-foreground">
              What's Included
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Comprehensive craftsmanship, down to every joint and surface
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            We handle every stage of your Decatur kitchen remodel under one roof, managing structural wall changes, fine wood cabinetry, bespoke stone fabrication, and final fixture commissioning with quiet precision.
          </p>
        </div>

        {/* Asymmetric 12-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left / Main Column (7 cols): Staggered Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            const isSpanned = i === 0; // First item spans 2 columns on tablet+ for visual architectural rhythm

            return <motion.div key={feature.id} variants={itemVariants} data-index={i} className={isSpanned ? "sm:col-span-2" : "sm:col-span-1"}>
                  <Card className="h-full bg-card/90 backdrop-blur-md border-border rounded-xl shadow-sm hover:shadow-md hover:border-chart-1/40 transition-[color,background-color,border-color,box-shadow] duration-300 relative overflow-hidden group">
                    {/* Architectural Copper Hairline Accent */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-chart-1 via-border to-transparent" />

                    <CardHeader className="pb-3 pt-6 px-6">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-chart-1 border border-border group-hover:bg-accent transition-colors">
                          <Icon className="w-5 h-5" strokeWidth={1.75} />
                        </div>
                        <Badge variant="secondary" className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground border-border">
                          {feature.tag}
                        </Badge>
                      </div>
                      <CardTitle className="font-serif text-lg sm:text-xl text-foreground font-semibold leading-snug">
                        {feature.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-6 pt-0 flex flex-col justify-between flex-1">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-4">
                        {feature.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border/60 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-chart-1 shrink-0 mt-0.5" />
                        <span className="text-xs font-medium text-foreground/80 leading-tight">
                          {feature.specs}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Column (5 cols): Stacked Project Imagery & Architectural Badge */}
          <div className="lg:col-span-5 flex flex-col gap-5 sticky top-24">
            {/* Top Showcase Image */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted group shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_cabinets_marble_island.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_cabinets_marble_island.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_cabinets_marble_island.webp-wvc-sizes" alt="Custom white oak cabinetry and honed marble waterfall island in Decatur bungalow" loading="lazy" decoding="async" className="w-full h-64 sm:h-72 object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-70" />
              <div className="absolute bottom-3 left-3 right-3 text-background">
                <p className="text-xs font-medium text-background/90">
                  Custom white oak joinery with honed Calacatta waterfall island
                </p>
              </div>
            </div>

            {/* Bottom Detail Image with Overlaid Frosted Glass Badge */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted group shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_brass_fixtures_backsplash.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_brass_fixtures_backsplash.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_brass_fixtures_backsplash.png-wvc-sizes" alt="Close-up detail of architectural brass fixtures, quartz backsplash, and task lighting" loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
              <div className="absolute inset-0 bg-foreground/20" />

              {/* Floating Frosted Glass Badge */}
              <div className="absolute bottom-4 inset-x-4 p-4 rounded-lg bg-card/95 backdrop-blur-md border border-border shadow-md">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-secondary text-chart-1 flex items-center justify-center shrink-0 border border-border">
                    <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground leading-tight">
                      Permitting & Inspection Handled
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      Every structural modification, electrical layout, and plumbing connection is submitted for city permitting and scheduled for required municipal inspections.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Box for Consultation */}
            <div className="p-6 rounded-xl bg-secondary/80 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-base font-serif font-semibold text-foreground">
                  Ready to plan your layout?
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Schedule an in-home architectural consultation in Decatur.
                </p>
              </div>
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 font-medium">
                <Link to="#" className="inline-flex items-center gap-1.5">
                  Book Visit
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}